export const getComponentOverrides = (palette) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollbarColor: `${palette.primary.main} transparent`,
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
          backgroundColor: 'transparent',
          width: '6px',
          height: '6px',
        },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          borderRadius: 8,
          backgroundColor: palette.primary.light,
          minHeight: 24,
        },
        '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
          backgroundColor: palette.primary.main,
        },
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: 'none',
        padding: '8px 16px',
        '&:hover': {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
        },
      },
      contained: {
        '&:active': { boxShadow: 'none' },
      },
      sizeLarge: {
        padding: '12px 24px',
        fontSize: '1rem',
      },
    },
  },

  // ─── Card ────────────────────────────────────────────────────────────────
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        boxShadow: '0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -1px rgba(0,0,0,0.06)',
        backgroundImage: 'none',
        overflow: 'hidden',
      },
    },
  },

  // ─── Card Header ─────────────────────────────────────────────────────────
  MuiCardHeader: {
    styleOverrides: {
      root: {
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText,
        padding: '14px 20px',
      },
      title: {
        color: palette.primary.contrastText,
        fontWeight: 700,
        fontSize: '1rem',
      },
      subheader: {
        color: `${palette.primary.contrastText}CC`, // 80% opacity
        fontSize: '0.8rem',
      },
      avatar: {
        // Avatar inside CardHeader also gets light treatment
        '& .MuiAvatar-root': {
          backgroundColor: `${palette.primary.contrastText}22`,
          color: palette.primary.contrastText,
          border: `2px solid ${palette.primary.contrastText}44`,
          fontWeight: 900,
        },
      },
      action: {
        alignSelf: 'center',
        marginTop: 0,
        marginRight: 0,
      },
    },
  },

  // ─── Card Content ────────────────────────────────────────────────────────
  MuiCardContent: {
    styleOverrides: {
      root: {
        '&:last-child': { paddingBottom: 16 },
      },
    },
  },

  // ─── Paper ────────────────────────────────────────────────────────────────
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      elevation1: {
        boxShadow: `0px 1px 3px rgba(0,0,0,0.12), 0px 1px 2px rgba(0,0,0,0.24)`,
      },
      elevation3: {
        // Panels/cards at elevation 3 get a subtle primary-tinted left border
        borderLeft: `4px solid ${palette.primary.main}`,
        boxShadow: `0px 4px 12px rgba(0,0,0,0.08)`,
      },
    },
  },

  // ─── Table ────────────────────────────────────────────────────────────────
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '12px 16px',
      },
      head: {
        fontWeight: 700,
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText,
        borderBottom: `2px solid ${palette.primary.dark}`,
      },
    },
  },

  // ─── DataGrid ─────────────────────────────────────────────────────────────
  MuiDataGrid: {
    styleOverrides: {
      root: {
        border: 'none',
        backgroundColor: palette.background.paper,
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          fontWeight: 700,
          borderBottom: `2px solid ${palette.primary.dark}`,
        },
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: palette.brand?.rowHover || palette.background.neutral,
        },
        '& .MuiDataGrid-row.Mui-selected': {
          backgroundColor: palette.primary.lighter,
        },
        '& .MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: palette.primary.light,
        },
        '& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton': {
          color: palette.primary.contrastText,
          opacity: 0.9,
        },
      },
    },
  },

  // ─── AppBar ───────────────────────────────────────────────────────────────
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0px 1px 1px rgba(0,0,0,0.05)',
        backgroundColor: palette.background.paper,
        color: palette.text.primary,
      },
    },
  },

  // ─── Drawer ───────────────────────────────────────────────────────────────
  // NOTE: Background is intentionally NOT set here — BarSesion.js sets it
  // via sx prop to 'primary.darker' so it responds to theme switches.
  MuiDrawer: {
    styleOverrides: {
      paper: {
        border: 'none',
      },
    },
  },

  // ─── List items ──────────────────────────────────────────────────────────
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 40,
        color: 'inherit',   // Inherit from parent so dark sidebar works correctly
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        marginBottom: 4,
        '&.Mui-selected': {
          backgroundColor: palette.primary.lighter,
          color: palette.primary.dark,
          '& .MuiListItemIcon-root': {
            color: palette.primary.dark,
          },
          '&:hover': {
            backgroundColor: palette.primary.lighter,
          },
        },
        '&:hover': {
          backgroundColor: palette.background.neutral || palette.background.default,
        },
      },
    },
  },

  // ─── Chip ────────────────────────────────────────────────────────────────
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 600,
      },
      colorPrimary: {
        backgroundColor: palette.primary.lighter,
        color: palette.primary.dark,
      },
    },
  },

  // ─── Divider ─────────────────────────────────────────────────────────────
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: palette.divider,
      },
    },
  },
});

